import { useCallback, useEffect, useState } from "react";

import { LogOut, UserRound } from "lucide-react";

import { LogoutButton } from "@/features/LogoutButton";

import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { useSessionStore } from "@/entities/session/store";
import { Button } from "@/shared/ui/button.tsx";
import {
  useMasterTeamleadsTeamleads,
  useTeamleadCredentials,
} from "@/entities/master_teamlead/api/api";
import { useMasterTeamleadStore } from "@/entities/master_teamlead";
import { Roles } from "@/entities/user";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import type { User } from "@/entities/user";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Modal } from "@/shared/ui/Modal";

export const MasterTeamleadModal = () => {
  const [open, setOpen] = useState(false);
  const { user, accessToken, refreshToken } = useSessionStore();
  const { accounts, addAccount } = useMasterTeamleadStore();
  const openModal = useCallback(() => setOpen(true), [setOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "b") {
        event.preventDefault();
        openModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [openModal]);

  useEffect(() => {
    if (user?.role === Roles.MasterTeamLead) {
      const masterLeadAccount = {
        user: {
          id: user._id,
          username: user.username,
          role: Roles.MasterTeamLead as const,
        },
        tokens: {
          access: accessToken!,
          refresh: refreshToken!,
        },
      };
      addAccount(masterLeadAccount);
    }
  }, [user, addAccount, refreshToken, accessToken]);

  const isMobile = useIsMobile();

  const teamleadAccountsData = useMasterTeamleadsTeamleads({
    masterId: user?._id as string,
    pageSize: 10,
    currentPage: 1,
    enabled: user?.role === Roles.MasterTeamLead,
  });

  if (user?.role === Roles.MasterTeamLead && teamleadAccountsData.isPending) {
    return <div>Загрузка аккаунтов...</div>;
  }

  if (user?.role === Roles.MasterTeamLead && teamleadAccountsData.isError) {
    return <div>Ошибка загрузки</div>;
  }

  const getAccountsData = () => {
    if (user?.role === Roles.MasterTeamLead && teamleadAccountsData.data) {
      return teamleadAccountsData.data.data.map((user) => ({
        id: user._id,
        username: user.userId.username,
      }));
    } else {
      return accounts.map((account) => ({
        id: account.user.id,
        username: account.user.username,
      }));
    }
  };

  const accountsForSwitch = getAccountsData();

  return isMobile ? (
    <DropdownMenu>
      <DropdownMenuTrigger>{user?.username}</DropdownMenuTrigger>
      <DropdownMenuContent>
        {accountsForSwitch
          .filter((account) => account.id !== user?._id)
          .map((account) => (
            <MasterLeadAccountSwitchModal
              key={account.id}
              accountId={account.id}
              role={user!.role as Roles.MasterTeamLead | Roles.TeamLead}
              isMobile={true}
            />
          ))}
        <div className="pt-1">
          <LogoutButton />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger>
          <div className="flex h-12 w-full items-center justify-between rounded-md bg-neutral-900 p-2">
            <div className="flex items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded bg-bgColor">
                <UserRound className="stroke-textColor" />
              </div>
              <div className="flex flex-col [line-height:1.2]">
                <span
                  className="max-w-44 truncate text-white"
                  style={{
                    lineHeight: "18px",
                  }}
                >
                  #{user?.username}
                </span>
                <p
                  className="text-left capitalize text-gray-300"
                  style={{
                    lineHeight: "14px",
                    fontSize: "12px",
                  }}
                >
                  {user?.role}
                </p>
              </div>
            </div>
            <LogOut className="stroke-white" size={16} />
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-textColor">
              Подтверждение выхода
            </DialogTitle>
            <DialogDescription className="text-lg">
              Вы действительно хотите выйти из аккаунта?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-2">
            <LogoutButton />

            <Modal.Close />
          </div>
        </DialogContent>
      </Dialog>
      <ScrollArea type="auto">
        <div className="flex max-h-96 flex-col gap-2 pr-4">
          {accountsForSwitch
            .filter((acc) => acc.id !== user?._id)
            .map((teamlead) => (
              <MasterLeadAccountSwitchModal
                key={teamlead.id}
                accountId={teamlead.id}
                role={user!.role as Roles.MasterTeamLead | Roles.TeamLead}
                isMobile={false}
              />
            ))}
        </div>
      </ScrollArea>
    </>
  );
};

const MasterLeadAccountSwitchModal = ({
  accountId,
  role,
  isMobile,
}: {
  accountId: string;
  role: Roles.TeamLead | Roles.MasterTeamLead;
  isMobile: boolean;
}) => {
  const { user, setUser, setAccessToken, setRefreshToken } = useSessionStore();

  const { addAccount, accounts } = useMasterTeamleadStore();

  const accountCredentialsFromApi = useTeamleadCredentials({
    accountId,
    enabled: !!accountId && role === Roles.MasterTeamLead,
  });

  if (accountCredentialsFromApi.isError)
    return <div>Ошибка загрузки авторизационных данных тимлида</div>;

  if (role === Roles.MasterTeamLead && accountCredentialsFromApi.data)
    addAccount(accountCredentialsFromApi.data.data);

  const accountsData =
    role === Roles.MasterTeamLead
      ? accountCredentialsFromApi.data?.data
      : accounts.find((acc) => acc.user.id === accountId);

  const handleAccountSwitch = () => {
    if (!accountsData) return;

    window.location.reload();

    setUser({
      ...user,
      _id: accountsData.user.id,
      code: "",
      username: accountsData.user.username,
      role: accountsData.user.role,
      permissions: [],
      accessToken: accountsData.tokens.access,
      refreshToken: accountsData.tokens.refresh,
    } as User);

    setAccessToken(accountsData.tokens.access);
    setRefreshToken(accountsData.tokens.refresh);
  };
  return isMobile ? (
    <DropdownMenuItem onClick={handleAccountSwitch}>
      {accountsData?.user.username} - {accountsData?.user.role}
    </DropdownMenuItem>
  ) : (
    <Dialog>
      <DialogTrigger>
        <div className="flex h-12 w-full items-center justify-start gap-2 rounded-md border border-neutral-200 p-2 shadow-sm">
          <div className="flex size-8 items-center justify-center rounded bg-bgGrayColor">
            <UserRound className="stroke-textColor" />
          </div>
          <div className="flex flex-col">
            <h2
              className="max-w-44 truncate text-textColor"
              style={{
                lineHeight: "18px",
              }}
            >
              #{accountsData?.user.username}
            </h2>
            <p
              className="text-left capitalize text-gray-500 text-textColor"
              style={{
                lineHeight: "14px",
                fontSize: "12px",
              }}
            >
              {accountsData?.user.role}
            </p>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-2">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-textColor">
            Подтверждение выхода
          </DialogTitle>
          <DialogDescription className="text-lg">
            Переключиться на аккаунт тимлида{" "}
            <strong className="text-black">
              {accountsData?.user.username}
            </strong>
            ?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-2">
          <Button
            className="w-full"
            disabled={
              accountCredentialsFromApi.isLoading ||
              accountCredentialsFromApi.isError
            }
            onClick={handleAccountSwitch}
          >
            {accountCredentialsFromApi.isLoading
              ? "Загрузка..."
              : "Переключить аккаунт"}
          </Button>

          <Modal.Close />
        </div>
      </DialogContent>
    </Dialog>
  );
};
